/**
 * PrivacyAppCard — one inert card per app for privacy mode.
 *
 * Shows the app icon, name, and a pre-formatted count line (e.g.
 * "5 messages"). No content, no sender, no actions, no click handler —
 * the card is a static list item. The host resolves the count string
 * (plural-aware i18n) and passes it in via `countLabel`, keeping this
 * component free of any i18n stack (matches the label-bag pattern used
 * across this package). Theme (light/dark) is handled entirely in CSS
 * via the design tokens in PrivacyAppCard.module.css — no theme prop.
 */

import { Avatar } from "../Avatar";
import styles from "./PrivacyAppCard.module.css";

export interface PrivacyAppCardProps {
  appName: string;
  /** Pre-formatted, plural-resolved count line, e.g. "5 messages". */
  countLabel: string;
  /** Resolved icon data-url (`base64ImageDataUrl(raw)`); omit to show the initial fallback. */
  iconDataUrl?: string;
}

export function PrivacyAppCard({ appName, countLabel, iconDataUrl }: PrivacyAppCardProps) {
  return (
    <li className={styles.card}>
      {/* Decorative icon (alt="") — the adjacent name span already announces
          the app, so a non-empty alt would make screen readers say it twice. */}
      {iconDataUrl ? (
        <Avatar src={iconDataUrl} alt="" />
      ) : (
        <Avatar initials={appName.charAt(0).toUpperCase()} />
      )}
      <div className={styles.text}>
        <span className={styles.name}>{appName}</span>
        <span className={styles.count}>{countLabel}</span>
      </div>
    </li>
  );
}
